// ============================================================================
// TEDIUX — Configuration du restaurant
// Modifiez facilement les informations ci-dessous (téléphone, horaires, menu).
// ============================================================================

export interface RestaurantConfig {
  name: string
  tagline: string
  slogan: string
  city: string
  address: string
  // Numéro WhatsApp au format international SANS le "+" ni espaces.
  // Bénin = 229. Exemple : 22990000000
  whatsappNumber: string
  phoneDisplay: string
  mapsQuery: string
}

export const RESTAURANT: RestaurantConfig = {
  name: 'Tediux',
  tagline: 'Des plats savoureux, préparés avec passion à Tangbé, Calavi.',
  slogan: 'Le goût qui vous fait revenir.',
  city: 'Calavi, Bénin',
  address: 'Tangbé, Calavi, Bénin',
  whatsappNumber: '22990000000',
  phoneDisplay: '+229 90 00 00 00',
  mapsQuery: 'Tangbé, Calavi, Bénin',
}

// ----------------------------------------------------------------------------
// HORAIRES — faciles à modifier
// ----------------------------------------------------------------------------
export interface OpeningHour {
  day: string
  hours: string
  closed?: boolean
}

export const OPENING_HOURS: OpeningHour[] = [
  { day: 'Lundi', hours: '10h00 – 22h00' },
  { day: 'Mardi', hours: '10h00 – 22h00' },
  { day: 'Mercredi', hours: '10h00 – 22h00' },
  { day: 'Jeudi', hours: '10h00 – 22h00' },
  { day: 'Vendredi', hours: '10h00 – 23h00' },
  { day: 'Samedi', hours: '11h00 – 23h00' },
  { day: 'Dimanche', hours: '14h00 – 22h00' },
]

// ----------------------------------------------------------------------------
// MENU
// ----------------------------------------------------------------------------
export interface MenuItem {
  id: string
  name: string
  description: string
  price: number // en FCFA
  image: string
  emoji: string
}

export interface MenuCategory {
  id: string
  name: string
  emoji: string
  items: MenuItem[]
}

export const MENU: MenuCategory[] = [
  {
    id: 'saucisses',
    name: 'Saucisses',
    emoji: '🌭',
    items: [
      {
        id: 'saucisse-grillee',
        name: 'Saucisse Grillée',
        description: 'Saucisse juteuse grillée au feu de bois, servie avec frites et sauce maison.',
        price: 1500,
        emoji: '🌭',
        image: 'https://images.unsplash.com/photo-1619740455993-9e612b1af08a?w=800&q=80',
      },
      {
        id: 'hot-dog-tediux',
        name: 'Hot-Dog Tediux',
        description: 'Pain moelleux, saucisse épicée, oignons caramélisés et ketchup piquant.',
        price: 1800,
        emoji: '🌭',
        image: 'https://images.unsplash.com/photo-1612392062631-94dd858cba88?w=800&q=80',
      },
      {
        id: 'saucisse-piquante',
        name: 'Saucisse Piquante',
        description: 'Pour les amateurs de piment : saucisse relevée, sauce pimentée du chef.',
        price: 2000,
        emoji: '🌶️',
        image: 'https://images.unsplash.com/photo-1529042410759-befb1204b468?w=800&q=80',
      },
    ],
  },
  {
    id: 'salades',
    name: 'Salades',
    emoji: '🥗',
    items: [
      {
        id: 'salade-fraicheur',
        name: 'Salade Fraîcheur',
        description: 'Légumes frais et croquants, œuf, thon et vinaigrette légère maison.',
        price: 2000,
        emoji: '🥗',
        image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=800&q=80',
      },
      {
        id: 'salade-cesar',
        name: 'Salade César',
        description: 'Poulet grillé, croûtons dorés, parmesan et sauce César crémeuse.',
        price: 2500,
        emoji: '🥗',
        image: 'https://images.unsplash.com/photo-1550304943-4f24f54ddde9?w=800&q=80',
      },
      {
        id: 'salade-avocat',
        name: 'Salade Avocat',
        description: 'Avocat crémeux, tomates, maïs doux et graines croustillantes.',
        price: 2800,
        emoji: '🥑',
        image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800&q=80',
      },
    ],
  },
  {
    id: 'shawarma',
    name: 'Shawarma',
    emoji: '🌯',
    items: [
      {
        id: 'shawarma-poulet',
        name: 'Shawarma Poulet',
        description: 'Poulet mariné, crudités fraîches et sauce à l’ail dans un pain doré.',
        price: 2000,
        emoji: '🌯',
        image: 'https://images.unsplash.com/photo-1633321088355-d0f81134ca3b?w=800&q=80',
      },
      {
        id: 'shawarma-viande',
        name: 'Shawarma Viande',
        description: 'Viande savoureuse, légumes croquants et sauce piquante maison.',
        price: 2500,
        emoji: '🌯',
        image: 'https://images.unsplash.com/photo-1615870216519-2f9fa575fa5c?w=800&q=80',
      },
      {
        id: 'shawarma-mixte',
        name: 'Shawarma Mixte',
        description: 'Le meilleur des deux : poulet & viande, généreusement garni.',
        price: 3000,
        emoji: '🌯',
        image: 'https://images.unsplash.com/photo-1665204089240-27f19a35eea9?w=800&q=80',
      },
    ],
  },
  {
    id: 'pizzas',
    name: 'Pizzas',
    emoji: '🍕',
    items: [
      {
        id: 'pizza-margherita',
        name: 'Pizza Margherita',
        description: 'Sauce tomate, mozzarella fondante et basilic frais. Un classique !',
        price: 3500,
        emoji: '🍕',
        image: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=800&q=80',
      },
      {
        id: 'pizza-poulet',
        name: 'Pizza Poulet',
        description: 'Poulet grillé, poivrons, oignons et fromage généreux.',
        price: 4000,
        emoji: '🍕',
        image: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=800&q=80',
      },
      {
        id: 'pizza-royale',
        name: 'Pizza Royale',
        description: 'Jambon, champignons, olives et double fromage. Un régal complet.',
        price: 4500,
        emoji: '🍕',
        image: 'https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=800&q=80',
      },
    ],
  },
  {
    id: 'yaourts',
    name: 'Yaourts',
    emoji: '🥛',
    items: [
      {
        id: 'yaourt-nature',
        name: 'Yaourt Nature',
        description: 'Yaourt maison onctueux, frais et légèrement sucré.',
        price: 1000,
        emoji: '🥛',
        image: 'https://images.unsplash.com/photo-1488477181946-6428a0291777?w=800&q=80',
      },
      {
        id: 'yaourt-fruits',
        name: 'Yaourt aux Fruits',
        description: 'Yaourt crémeux garni de fruits frais de saison.',
        price: 1500,
        emoji: '🍓',
        image: 'https://images.unsplash.com/photo-1502741224143-90386d7f8c82?w=800&q=80',
      },
      {
        id: 'yaourt-granola',
        name: 'Yaourt Granola',
        description: 'Yaourt, granola croustillant et miel doré. Énergie garantie.',
        price: 1800,
        emoji: '🥣',
        image: 'https://images.unsplash.com/photo-1490474418585-ba9bad8fd0ea?w=800&q=80',
      },
    ],
  },
  {
    id: 'specialites',
    name: 'Autres spécialités',
    emoji: '✨',
    items: [
      {
        id: 'burger-tediux',
        name: 'Burger Tediux',
        description: 'Steak juteux, cheddar fondant, salade croquante et sauce signature.',
        price: 3000,
        emoji: '🍔',
        image: 'https://images.unsplash.com/photo-1550547660-d9450f859349?w=800&q=80',
      },
      {
        id: 'frites-poulet',
        name: 'Poulet Frites',
        description: 'Poulet grillé savoureux accompagné de frites croustillantes.',
        price: 2500,
        emoji: '🍗',
        image: 'https://images.unsplash.com/photo-1562967914-608f82629710?w=800&q=80',
      },
      {
        id: 'jus-naturel',
        name: 'Jus Naturel',
        description: 'Jus de fruits frais pressés : ananas, orange ou bissap.',
        price: 1000,
        emoji: '🧃',
        image: 'https://images.unsplash.com/photo-1600271886742-f049cd451bba?w=800&q=80',
      },
    ],
  },
]

// Liste plate pratique pour le formulaire de commande.
export const ALL_ITEMS: MenuItem[] = MENU.flatMap((category) => category.items)

export function formatFCFA(amount: number): string {
  return `${amount.toLocaleString('fr-FR')} FCFA`
}

// Galerie — sélection de belles photos appétissantes.
export const GALLERY: string[] = [
  'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=800&q=80',
  'https://images.unsplash.com/photo-1633321088355-d0f81134ca3b?w=800&q=80',
  'https://images.unsplash.com/photo-1550547660-d9450f859349?w=800&q=80',
  'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=800&q=80',
  'https://images.unsplash.com/photo-1619740455993-9e612b1af08a?w=800&q=80',
  'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=800&q=80',
]
